<template>
  <section class="optimization-example">
    <button class="back-link" type="button" @click="goBack">
      <a-icon type="left" />
      {{ $t("ExBack") }}
    </button>

    <section class="overview-card">
      <h1>
        <span class="play-mark">▶</span>
        {{ $t("ExOverviewTitle") }}

      </h1>
      <p class="sub-copy" v-html="$t('ExSubCopy')"></p>

      <div class="pain-grid">
        <div class="pain-column">
          <h2>{{ $t("ExPainManualTitle") }}</h2>
        </div>
        <div class="pain-head-spacer" aria-hidden="true"></div>
        <div class="pain-column pain-column-sb">
          <h2>{{ $t("ExPainSBTitle") }}</h2>
        </div>
        <template v-for="(row, index) in painRows">
          <div class="manual-row" :key="'manual-' + index">
            <span>{{ row.problem.icon }}</span>
            <div>
              <strong>{{ row.problem.title }}</strong>
              <p>{{ row.problem.text }}</p>
            </div>
          </div>
          <span class="pain-arrow" :key="'arrow-' + index">→</span>
          <div class="auto-row" :key="'auto-' + index">
            <span>{{ row.benefit.icon }}</span>
            <div>
              <strong>{{ row.benefit.title }}</strong>
              <p>{{ row.benefit.text }}</p>
            </div>
          </div>
        </template>
      </div>

      <div class="industry-row">
        <strong>{{ $t("ExAnyIndustry") }}</strong>
        <span v-for="industry in industries" :key="industry">
          {{ industry }}
        </span>
        <em>{{ $t("ExAnyIndustryNote") }}</em>
      </div>
    </section>

    <section class="step-card">
      <header class="step-title">
        <span>1</span>
        <h2 v-html="$t('ExStep1Title')"></h2>
      </header>

      <div class="target-flow">
        <article class="flow-box account">
          <div class="flow-icon">👤</div>
          <h3>{{ $t("ExFlowAccountTitle") }}</h3>
          <strong>{{ $t("ExFlowAccountStrong") }}</strong>
          <p>{{ $t("ExFlowAccountP") }}</p>
        </article>
        <span class="flow-arrow">→</span>
        <article class="flow-box source">
          <div class="flow-icon">🤖</div>
          <h3>{{ $t("ExFlowSourceTitle") }}</h3>
          <strong>{{ $t("ExFlowSourceStrong") }}</strong>
          <p>{{ $t("ExFlowSourceP") }}</p>
        </article>
        <span class="flow-arrow">→</span>
        <article class="flow-box customers">
          <div class="flow-icon">📥</div>
          <h3>{{ $t("ExFlowCustomersTitle") }}</h3>
          <p v-html="$t('ExFlowCustomersP')"></p>
        </article>
      </div>

      <div class="avatar-cluster">
        <span v-for="avatar in avatars" :key="avatar">{{ avatar }}</span>
        <b>+235</b>
      </div>
      <p class="hint">{{ $t("ExHint") }}</p>
    </section>

    <section class="step-card chat-step">
      <header class="step-title">
        <span>2</span>
        <h2 v-html="$t('ExStep2Title')"></h2>
      </header>

      <div class="feature-bar">
        <span v-for="feature in chatFeatures" :key="feature"
          >✅ {{ feature }}</span
        >
      </div>
      <p class="chat-copy" v-html="$t('ExChatCopy')"></p>

      <div class="chat-grid">
        <article
          class="conversation-card"
          v-for="conversation in conversations"
          :key="conversation.name"
        >
          <div class="conversation-head">
            <div class="person">
              <span>{{ conversation.initial }}</span>
              <div>
                <h3>{{ conversation.name }}</h3>
                <p>{{ conversation.role }}</p>
              </div>
            </div>
            <strong>
              <span class="linkedin-mark">in</span>
              {{ $t("ExInYourLinkedIn") }}
            </strong>
          </div>

          <div class="conversation-body">
            <div class="conversation-scroll">
              <p class="account-note">{{ $t("ExAccountNote") }}</p>
              <div
                v-for="message in conversation.messages"
                :key="message.text"
                :class="['message', message.from]"
              >
                <small v-if="message.label">{{ message.label }}</small>
                <p v-html="message.text"></p>
              </div>
            </div>
            <button type="button" @click="openConversation(conversation)">
              {{ $t("ExClickEnlarge") }}
            </button>
          </div>

          <footer>✅ {{ conversation.footer }}</footer>
        </article>
      </div>

      <p class="footnote">{{ $t("ExFootnote") }}</p>
    </section>

    <section class="step-card result-step">
      <header class="step-title">
        <span>3</span>
        <h2>{{ $t("ExStep3Title") }}</h2>
      </header>
      <p class="result-copy" v-html="$t('ExResultCopy')"></p>

      <div class="metric-grid">
        <article>
          <span>📄</span>
          <strong>8</strong>
          <p>{{ $t("ExMetricInterested") }}</p>
        </article>
        <article>
          <span>📞</span>
          <strong class="orange">5</strong>
          <p>{{ $t("ExMetricContact") }}</p>
        </article>
      </div>

      <div class="lead-list">
        <div class="lead-row" v-for="lead in leads" :key="lead.name">
          <div class="lead-person">
            <span>{{ lead.initial }}</span>
            <div>
              <h3>{{ lead.name }}</h3>
              <p>{{ lead.role }}</p>
            </div>
          </div>
          <div class="lead-tags">
            <em
              v-for="tag in lead.tags"
              :key="tag"
              :class="tagClass(tag)"
            >
              {{ $t(tag) }}
            </em>
          </div>
        </div>
      </div>

      <p class="result-note">{{ $t("ExResultNote") }}</p>
    </section>

    <section class="cta-card">
      <p>{{ $t("ExCtaCopy") }}</p>
      <a-button class="cta-button" @click="handleCreateCampaign">
        {{ $t("ExCtaButton") }}
        <a-icon type="arrow-right" />
      </a-button>
    </section>

    <div
      v-if="activeConversation"
      class="conversation-modal-mask"
      role="dialog"
      aria-modal="true"
    >
      <section class="conversation-modal">
        <div class="conversation-head modal-head">
          <div class="person">
            <span>{{ activeConversation.initial }}</span>
            <div>
              <h3>{{ activeConversation.name }}</h3>
              <p>{{ activeConversation.role }}</p>
            </div>
          </div>
          <strong>
            <span class="linkedin-mark">in</span>
            {{ $t("ExInYourLinkedIn") }}
          </strong>
          <button
            class="modal-close"
            type="button"
            :aria-label="$t('ExModalClose')"
            @click="closeConversation"
          >
            <a-icon type="close" />
          </button>
        </div>

        <div class="conversation-body modal-body">
          <p class="account-note">{{ $t("ExAccountNote") }}</p>
          <div
            v-for="message in activeConversation.messages"
            :key="message.text"
            :class="['message', message.from]"
          >
            <small v-if="message.label">{{ message.label }}</small>
            <p>{{ message.text }}</p>
          </div>
        </div>

        <footer>✅ {{ activeConversation.footer }}</footer>
      </section>
    </div>
  </section>
</template>

<script>
export default {
  name: "RecruiterExample",
  i18n: require("./recruiterExamplei18n"),
  data() {
    return {
      activeConversation: null,
      avatars: ["M", "S", "J", "A", "L"],
    };
  },
  watch: {
    // 切换语言时，把弹窗内打开的对话同步替换为对应语言的新数据
    "$i18n.locale"() {
      if (this.activeConversation) {
        let name = this.activeConversation.name;
        this.activeConversation =
          this.conversations.find((item) => item.name === name) || null;
      }
    },
  },
  computed: {
    manualProblems() {
      return [
        {
          icon: "🕰️",
          title: this.$t("ExProblem1Title"),
          text: this.$t("ExProblem1"),
        },
        {
          icon: "🕳️",
          title: this.$t("ExProblem2Title"),
          text: this.$t("ExProblem2"),
        },
        {
          icon: "💸",
          title: this.$t("ExProblem3Title"),
          text: this.$t("ExProblem3"),
        },
        {
          icon: "📧",
          title: this.$t("ExProblem4Title"),
          text: this.$t("ExProblem4"),
        },
      ];
    },
    strategyBenefits() {
      return [
        {
          icon: "⚡",
          title: this.$t("ExBenefit1Title"),
          text: this.$t("ExBenefit1"),
        },
        {
          icon: "🎯",
          title: this.$t("ExBenefit2Title"),
          text: this.$t("ExBenefit2"),
        },
        {
          icon: "✅",
          title: this.$t("ExBenefit3Title"),
          text: this.$t("ExBenefit3"),
        },
        {
          icon: "📋",
          title: this.$t("ExBenefit4Title"),
          text: this.$t("ExBenefit4"),
        },
      ];
    },
    industries() {
      return [
        this.$t("ExIndustry1"),
        this.$t("ExIndustry2"),
        this.$t("ExIndustry3"),
        this.$t("ExIndustry4"),
        this.$t("ExIndustry5"),
        this.$t("ExIndustry6"),
        this.$t("ExIndustry7"),
        this.$t("ExIndustry8"),
      ];
    },
    chatFeatures() {
      return [
        this.$t("ExChatFeature1"),
        this.$t("ExChatFeature2"),
        this.$t("ExChatFeature3"),
        this.$t("ExChatFeature4"),
      ];
    },
    conversations() {
      return [
        {
          initial: "M",
          name: "Michael Kowalski",
          role: 'Procurement Manager (Technical & Project Leadership)',
          footer: this.$t("ExConvFooter1"),
          messages: [
            {
              from: "ai",
              text: `Hi Michael,<br/>I'm Ashley Bennett, and I came across your impressive background with strong offshore wind project experience and leadership in technical procurement and fleet management. We're currently looking for a General Manager - Offshore Wind Installation who will report directly to our Vice President and oversee comprehensive project installations,from planning and safety management to on-site coordination and stakeholder engagement.<br/>          Your extensive experience in managing technical risks, coordinating large-scale projects, and driving operational excellence strongly aligns with what we're seeking. Would you be interested in discussing this opportunity further?<br/>Looking forward to your thoughts.`,
            },
            {
              from: "user",
              label: "Michael Kowalski",
              text: `Dear Ashley,<br/>Thank you for reaching out to me. I appreciate your kind words about my background and experience.<br/>Yes, I am keen on this opportunity and would be glad to discuss it further.<br/>Best regards,Michael`,
            },
            {
              from: "ai",
              text: `Michael, here's a clear overview of the General Manager, Offshore Wind Installation role we're discussing.<br/>          The role centers on full-scale management of offshore wind installation projects, including planning, design, and on-site execution. You'll be responsible for developing specialized installation schemes and managing everything from resource allocation and construction schedules to stringent safety and compliance protocols. Our objective is to deliver projects on time while meeting quality and safety standards, resolving complex technical issues related to marine operations and harsh offshore environments.<br/>          Given your background in technical procurement leadership and experience managing large-scale offshore wind EPCI projects, this position aligns well with your expertise. It also provides a pathway to broaden your leadership responsibilities, as you'll lead both internal teams and external subcontractors while interfacing with key stakeholders to drive overall project success. The role is based in Boston, Massachusetts, and is designed for someone who can apply deep technical insight and operational aptitude to manage and optimize offshore wind installations.<br/>          Are the details clear, or is there anything specific in the role you'd like to discuss further?`,
            },
            {
              from: "user",
             label: "Michael Kowalski",
              text:`Hi, just now I tried to add your WhatsApp , but the ID seems incorrect. Can u resent the correct one or U may add My WhatsApp: +10000000000?` ,
            },
            {
              from: "ai",
              text: `Michael, I'll need to verify the correct WhatsApp with our internal team. Meanwhile, I'll add your provided WhatsApp to initiate communication on that platform. Let's stay connected and continue our discussion directly."`,
            },
           
          ],
        },
        {
          initial: "A",
          name: "Arjun Patel",
          role: 'Process Head – Global Immigration, Documentation & Social Media Operations',
          footer: this.$t("ExConvFooter2"),
          messages: [
            {
              from: "ai",
              text: `Hello Arjun, I'm Sharma from Competitive School Ltd. I noticed your impressive background in global immigration and visa operations,particularly your work with UK visa processes and student admissions. We're currently expanding our team for two key roles: Senior Student Visa Counselor, UK and Assistant Manager, UK Division. Given your expertise, I think you could be a great fit for one of these positions. Would you be interested in discussing this opportunity further?`,
            },
            {
              from: "user",
              label: "Arjun Patel",
              text: `Yes, I'm interested`,
            },
            {
              from: "ai",
              text: `Arjun, I'd like to get a deeper understanding of your current situation. What specific challenges or roadblocks are you experiencing in your current role? For instance, are there any issues with team dynamics, skill development, or career growth that you'd like to overcome?`,
            },
            {
              from: "user",
              label: "Arjun Patel",
              text: `Thank you for asking. One of the main reasons I am exploring new opportunities is to gain better career growth and learning opportunities. I am looking for a role where I can expand my knowledge, develop new skills, take on greater responsibilities, and continue growing professionally. While I have gained valuable experience in my current role, I believe exposure to new challenges and environments will help me enhance my capabilities and achieve my long-term career goals.`,
            },
            {
              from: "ai",
              text: `Arjun, let me share some specifics about these roles. The Senior Student Visa Counselor position primarily involves one-on-one counseling with students, guiding them through the complete UKVI student visa process, including the CAS procedure, financial documentation, and adherence to compliance. You'll be working to meet enrollment targets, maintain detailed CRM records, and handle student concerns,from visa compliance to clarifying financial planning issues. The position also emphasizes an in-depth understanding of UK universities and the Graduate Route visa, with growth potential to progress into managerial roles over time.<br/>          On the other hand, the Assistant Manager role in the UK Division is designed for someone who can lead and mentor a team of counselors. This role has a strong focus on team performance,monitoring KPIs, overseeing enrollment pipelines, and coordinating with UK university partners. You’ll also be directly involved in developing business initiatives and training junior staff on the latest UKVI regulations and best practices. There’s a clear growth trajectory here, with the potential to eventually head the UK Division.<br/>          Both roles offer opportunities for significant career growth, continuous skills development through regular training, and a chance to expand your professional network within the education sector. Understanding your background in global immigration and your drive for professional growth, these roles could align well with your aspirations.<br/>          Is this information clear, or would you like any further details about the position responsibilities or growth opportunities?`,
            },
            {
              from: "user",
              label: "Arjun Patel",
              text: `Enough description it is.`,
            },
            {
              from: "user",
              label: "Arjun Patel",
              text: `I am interested in Assistant Manager`,
            },
             {
              from: "ai",
              text: `Aishwarya, to move things forward, drop one contact detail,be it your phone number, email, or any other preferred channel, so we can set up an interview. If you've got your resume handy, feel free to attach that too.`,
            },
            {
              from: "user",
              label: "Arjun Patel",
              text: 'Sure',
            },
            {
              from: "user",
              label: "Arjun Patel",
              text: '+1600000000',
            },
            {
              from: "user",
              label: `Resume - <strong>Arjun Patel</strong>.pdf`,
              text: 'Sure',
            },
            {
              from: "ai",
              text: `Arjun, I've got your resume and phone number. I'll be reviewing your details shortly, and we'll set up a call soon to discuss the Assistant Manager role in more depth. Look forward to speaking with you."`,
            },
          ],
        },
      ];
    },
    leads() {
      return [
        {
          initial: "M",
          name: "Mark T.",
          role: this.$t("ExLead1Role"),
          tags: ["Resume", "Phone"],
        },
        {
          initial: "L",
          name: "Lucia R.",
          role: this.$t("ExLead2Role"),
          tags: ["Resume", "Phone"],
        },
        {
          initial: "A",
          name: "Anna K.",
          role: this.$t("ExLead3Role"),
          tags: ["Resume", "Phone"],
        },
        {
          initial: "D",
          name: "David R.",
          role: this.$t("ExLead4Role"),
          tags: ["Resume", "Phone"],
        },
        {
          initial: "P",
          name: "Priya N.",
          role: this.$t("ExLead5Role"),
          tags: ["Resume", "Phone"],
        },
      ];
    },
    painRows() {
      return this.manualProblems.map((problem, index) => ({
        problem,
        benefit: this.strategyBenefits[index],
      }));
    },
  },
  methods: {
    handleCreateCampaign() {
      let { campaign_type } = this.$route.query;
      //   let { hr_campaign_created, sales_campaign_created } = this.user || {};
      this.writeLog(
        "consultation",
        campaign_type == "1" ? "sales" : "recruiter",
        "continue_consultation",
      );
      if (campaign_type == "1") {
        if (this.user.has_customer_info) {
          this.$router.push({
            path: "/sales/salesReps/createCampaign",
            query: { linkedin_id: "new" },
          });
        } else {
          this.$router.push({
            path: "/sales/salesReps/collectUserInfo",
            query: { linkedin_id: "new" },
          });
        }
      } else {
        if (this.user.has_customer_info) {
          this.$router.replace({
            path: "/hr/recruiter/createCampaign",
            query: { linkedin_id: "new" },
          });
        } else {
          this.$router.replace({
            path: "/hr/recruiter/collectUserInfo",
            query: { linkedin_id: "new" },
          });
        }
      }
    },
    goBack() {
      this.$router.go(-1);
    },
    openConversation(conversation) {
      this.activeConversation = conversation;
      this.$emit("open-transcript", conversation);
    },
    closeConversation() {
      this.activeConversation = null;
    },
    tagClass(tag) {
      return tag.toLowerCase();
    },
  },
};
</script>

<style lang="less" scoped>
.optimization-example {
  min-height: 100%;
  padding: 34px 32px;
  background: #f0f4f1;
  color: #263533;
  font-family: Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Arial,
    sans-serif;
}

.back-link {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin: 0 0 24px;
  padding: 0;
  border: 0;
  background: transparent;
  color: #11836f;
  font-size: 20px;
  line-height: 1;
  font-weight: 800;
  cursor: pointer;
}

.overview-card,
.step-card,
.cta-card {
  border: 1px solid #dfe7e3;
  border-radius: 14px;
  background: #fff;
  box-shadow: 0 8px 24px rgba(21, 43, 38, 0.06);
}

.overview-card {
  padding: 24px;

  h1 {
    display: flex;
    align-items: center;
    gap: 12px;
    margin: 0;
    color: #1c2927;
    font-size: 19px;
    line-height: 1.25;
    font-weight: 900;
  }
}

.play-mark,
.step-title span {
  color: #078f78;
}

.sub-copy {
  margin: 10px 0 18px;
  color: #98a19e;
  font-size: 14px;
  line-height: 1.4;
  /deep/ strong { 
    color: #087764;
  }
  strong {
    color: #087764;
  }
}

.pain-grid {
  display: grid;
  grid-template-columns: 1fr 34px 1fr;
  gap: 10px 14px;
  align-items: center;
}

.pain-column {
  h2 {
    margin: 0 0 10px;
    color: #087764;
    font-size: 14px;
    line-height: 1.2;
    font-weight: 900;
  }
}

.manual-row,
.auto-row {
  display: flex;
  align-items: center;
  gap: 12px;
  min-height: 42px;
  padding: 10px 14px;
  border-radius: 8px;
  font-size: 13px;
  line-height: 1.35;

  > span {
    flex-shrink: 0;
    font-size: 20px;
    line-height: 1;
  }

  strong {
    display: block;
    margin-bottom: 2px;
    font-weight: 900;
  }

  p {
    margin: 0;
  }
}

.manual-row {
  border: 1px solid #f4c6c1;
  background: #fff0ef;
  color: #a64e48;

  strong {
    color: #7a3730;
  }
}

.auto-row {
  border: 1px solid #cfe9e3;
  background: #effbf8;
  color: #49625f;

  /deep/ strong {
    color: #2d3d3a;
    font-weight: 900;
  }
}

.pain-arrow {
  display: flex;
  align-items: center;
  justify-content: center;
  color: #087764;
  font-size: 22px;
  font-weight: 900;
}

.industry-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-top: 18px;
  color: #65716e;
  font-size: 13px;
  font-weight: 700;

  span,
  em {
    display: inline-flex;
    align-items: center;
    min-height: 31px;
    padding: 0 16px;
    border: 1px solid #dfe7e5;
    border-radius: 17px;
    background: #fff;
    font-style: normal;
  }

  em {
    border-color: #c7e4dd;
    background: #dff4ef;
    color: #087764;
    font-weight: 900;
  }
}

.step-card {
  margin-top: 14px;
  padding: 22px 24px;
}

.step-title {
  display: flex;
  align-items: center;
  gap: 10px;

  span {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 26px;
    height: 26px;
    border-radius: 50%;
    background: #0b8d77;
    color: #fff;
    font-size: 14px;
    font-weight: 900;
  }

  h2 {
    margin: 0;
    color: #263533;
    font-size: 18px;
    line-height: 1.35;
    font-weight: 900;
  }

   /deep/strong {
    color: #0b8d77;
  }

  .blue {
    color: #0874c9;
  }
}

.target-flow {
  display: grid;
  grid-template-columns: 1fr 28px 1fr 28px 1fr;
  align-items: center;
  gap: 12px;
  margin-top: 22px;
}

.flow-box {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 148px;
  padding: 18px;
  border: 2px solid #dfe7e5;
  border-radius: 12px;
  text-align: center;

  h3 {
    margin: 12px 0 4px;
    color: #34433f;
    font-size: 15px;
    font-weight: 900;
  }

  strong {
    color: #0a7d6b;
    font-size: 13px;
  }

  p {
    margin: 3px 0 0;
    color: #a0aaa7;
    font-size: 12px;
    line-height: 1.25;
  }
}

.flow-box.account {
  border-color: #0e8d77;
  background: #e8f7f3;
}

.flow-box.source {
  border-color: #ff6b2a;
  background: #fff;

  strong {
    color: #ff6b2a;
  }
}

.flow-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: #f2f7f5;
  font-size: 21px;
}

.flow-arrow {
  color: #0a8b75;
  font-size: 30px;
  font-weight: 900;
  text-align: center;
}

.avatar-cluster {
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 16px;

  span,
  b {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 25px;
    height: 25px;
    margin-left: -5px;
    border-radius: 50%;
    background: #ccece4;
    color: #0d806d;
    font-size: 11px;
    font-weight: 900;
  }

  b {
    background: #6f7d79;
    color: #fff;
    width:40px;
  }
}

.hint,
.footnote,
.result-note {
  margin: 12px 0 0;
  color: #a1aaa7;
  font-size: 12px;
  line-height: 1.45;
  text-align: center;
}

.feature-bar {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 8px;
  margin-top: 22px;
  padding: 14px 12px;
  border-radius: 6px;
  background: #0875c9;
  color: #fff;
  font-size: 12px;
  font-weight: 900;
}

.chat-copy {
  margin: 18px 0;
  color: #9aa4a1;
  font-size: 13px;

  strong {
    color: #0874c9;
  }
}

.chat-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
}

.conversation-card {
  overflow: hidden;
  border: 1px solid #e0e7e4;
  border-radius: 12px;
  background: #eaf1f6;
}

.conversation-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 18px;
  background: #fff;

  strong {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    color: #0874c9;
    font-size: 12px;
  }
}

.linkedin-mark {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 15px;
  height: 15px;
  border-radius: 3px;
  background: #0a66c2;
  color: #fff;
  font-size: 10px;
  font-weight: 900;
  line-height: 1;
}

.person,
.lead-person {
  display: flex;
  align-items: center;
  gap: 12px;

  > span {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 38px;
    height: 38px;
    border-radius: 50%;
    background: #edf6fb;
    color: #1379cf;
    font-weight: 900;
  }

  h3,
  p {
    margin: 0;
  }

  h3 {
    color: #34413f;
    font-size: 14px;
    font-weight: 900;
  }

  p {
    margin-top: 2px;
    color: #8e9996;
    font-size: 12px;
  }
}

.conversation-body {
  position: relative;
  display: flex;
  flex-direction: column;
  height: 650px;
  padding: 0;

  .account-note {
    margin: 0 0 12px;
    color: #8e9996;
    font-size: 11px;
    font-weight: 700;
  }

  button {
    z-index: 2;
    display: block;
    min-width: 330px;
    height: 44px;
    margin: 0 auto 18px;
    border: 0;
    border-radius: 22px;
    background: #0874c9;
    color: #fff;
    font-size: 13px;
    font-weight: 800;
    cursor: pointer;
    box-shadow: 0 10px 24px rgba(8, 116, 201, 0.24);
  }
}

.conversation-scroll {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 18px 18px 14px;
  scroll-behavior: smooth;
}

.message {
  margin-top: 12px;

  small {
    display: block;
    margin-bottom: 4px;
    color: #899491;
    font-size: 11px;
  }

  p {
    max-width: 82%;
    margin: 0;
    padding: 12px 14px;
    border-radius: 11px;
    font-size: 13px;
    line-height: 1.38;
  }
}

.message.ai {
  display: flex;
  justify-content: flex-end;

  p {
    background: #0874c9;
    color: #fff;
  }
}

.message.user p {
  background: #fff;
  color: #54615e;
}

.conversation-card footer {
  padding: 10px 16px;
  background: #e2f5ee;
  color: #0b8d3f;
  font-size: 12px;
  font-weight: 900;
}

.conversation-modal-mask {
  position: fixed;
  inset: 0;
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 30px;
  background: rgba(0, 0, 0, 0.62);
}

.conversation-modal {
  overflow: hidden;
  width: 920px;
  max-height: calc(100vh - 60px);
  border-radius: 18px;
  background: #eaf1f6;
  box-shadow: 0 24px 70px rgba(0, 0, 0, 0.32);

  footer {
    padding: 16px 24px;
    background: #e2f5ee;
    color: #0b8d3f;
    font-size: 15px;
    font-weight: 900;
  }
}

.modal-head {
  position: relative;
  min-height: 88px;
  padding: 22px 68px 22px 24px;

  .person {
    > span {
      width: 56px;
      height: 56px;
      font-size: 20px;
    }

    h3 {
      font-size: 22px;
    }

    p {
      font-size: 16px;
    }
  }

  strong {
    margin-left: auto;
    font-size: 14px;
  }
}

.modal-close {
  position: absolute;
  top: 28px;
  right: 24px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 42px;
  border: 0;
  border-radius: 50%;
  background: #dfe8ee;
  color: #4b5554;
  font-size: 18px;
  cursor: pointer;
}

.modal-body {
  display: block;
  height: auto;
  overflow-y: auto;
  max-height: calc(100vh - 238px);
  padding: 28px 24px 26px;

  .account-note {
    font-size: 14px;
  }

  .message {
    margin-top: 18px;

    small {
      font-size: 14px;
    }

    p {
      padding: 14px 18px;
      border-radius: 13px;
      font-size: 18px;
      line-height: 1.45;
    }
  }
}

.result-copy {
  margin: 22px 0 0;
  color: #97a19e;
  font-size: 14px;

  /deep/strong {
    color: #0c8270;
  }
}

.metric-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
  margin-top: 22px;

  article {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 120px;
    border: 1px solid #e0e7e4;
    border-radius: 10px;
    background: #fff;
  }

  span {
    font-size: 20px;
  }

  strong {
    margin-top: 8px;
    color: #08816e;
    font-size: 30px;
    line-height: 1;
    font-weight: 900;
  }

  .orange {
    color: #ff6828;
  }

  p {
    margin: 12px 0 0;
    color: #9aa4a1;
    font-size: 12px;
  }
}

.lead-list {
  margin-top: 22px;
}

.lead-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 62px;
  border-bottom: 1px dashed #e3e9e6;
}

.lead-person > span {
  background: #0b8d77;
  color: #fff;
}

.lead-tags {
  display: flex;
  gap: 8px;

  em {
    display: inline-flex;
    align-items: center;
    min-height: 24px;
    padding: 0 10px;
    border-radius: 8px;
    font-size: 12px;
    font-style: normal;
    font-weight: 900;
  }

  .interested {
    background: #e3f6ef;
    color: #0a846f;
  }

  .resume {
    background: #eef1f0;
    color: #5c6763;
  }

  .email,
  .phone {
    background: #fff0e7;
    color: #ec6b2d;
  }
}

.cta-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  margin-top: 18px;
  padding: 24px;
  background: #e5f8f4;

  p {
    margin: 0;
    color: #0b7665;
    font-size: 18px;
    line-height: 1.35;
    font-weight: 900;
  }
}

.cta-button {
  min-width: 230px;
  height: 56px;
  border: 0;
  border-radius: 9px;
  background: #0a8b75;
  color: #fff;
  font-size: 16px;
  font-weight: 900;
}

@media (max-width: 1100px) {
  .pain-grid,
  .target-flow,
  .chat-grid,
  .metric-grid {
    grid-template-columns: 1fr;
  }

  .pain-arrow,
  .pain-head-spacer,
  .flow-arrow {
    display: none;
  }

  .auto-row,
  .pain-column-sb {
    order: 2;
  }

  .feature-bar {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .conversation-body {
    min-height: auto;
  }
}

@media (max-width: 680px) {
  .optimization-example {
    padding: 20px 14px;
  }

  .overview-card,
  .step-card,
  .cta-card {
    border-radius: 10px;
  }

  .pain-grid,
  .feature-bar {
    grid-template-columns: 1fr;
  }

  .conversation-body button {
    min-width: 0;
    width: calc(100% - 32px);
  }

  .cta-card {
    align-items: stretch;
    flex-direction: column;
  }

  .cta-button {
    width: 100%;
  }
}
</style>
